import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Target, TrendingUp, Award, Flame, Users, Plus, Calendar, Play } from 'lucide-react';
import GoalCard from './components/GoalCard';
import CreateGoalModal from './components/CreateGoalModal';
import ProgressOverview from './components/ProgressOverview';
import MilestoneTracker from './components/MilestoneTracker';
import StreakDisplay from './components/StreakDisplay';
import LeaderboardPanel from './components/LeaderboardPanel';
import AnalyticsVisualization from './components/AnalyticsVisualization';
import { getActiveGoals, getPracticeStreak, getAchievementBadges, getGoalProgressSummary, getLeaderboard } from '../../services/studentGoalsService';
import { realtimeService } from '../../services/realtimeSubscriptionService';
import { useAuth } from '../../contexts/AuthContext';

const StudentGoalSettingCenter = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [streak, setStreak] = useState(null);
  const [badges, setBadges] = useState([]);
  const [summary, setSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('goals');

  useEffect(() => {
    loadData();
  }, []);

  // Real-time subscriptions
  useEffect(() => {
    if (!user?.id) return;

    console.log('Setting up real-time subscriptions for user:', user?.id);

    // Subscribe to goals changes
    const unsubscribeGoals = realtimeService?.subscribeToGoals(user?.id, (payload) => {
      console.log('Real-time goal update:', payload);
      loadGoalsData();
    });

    // Subscribe to achievements changes
    const unsubscribeAchievements = realtimeService?.subscribeToAchievements(user?.id, (payload) => {
      console.log('Real-time achievement update:', payload);
      loadAchievementsData();
    });

    // Subscribe to streak changes
    const unsubscribeStreak = realtimeService?.subscribeToStreak(user?.id, (payload) => {
      console.log('Real-time streak update:', payload);
      loadStreakData();
    });

    // Subscribe to leaderboard changes
    const unsubscribeLeaderboard = realtimeService?.subscribeToLeaderboard((payload) => {
      console.log('Real-time leaderboard update:', payload);
      loadLeaderboardData();
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeGoals();
      unsubscribeAchievements();
      unsubscribeStreak();
      unsubscribeLeaderboard();
    };
  }, [user?.id]);

  // Subscribe to milestones when goals change
  useEffect(() => {
    if (!goals || goals?.length === 0) return;

    const goalIds = goals?.map(g => g?.id)?.filter(Boolean);
    if (goalIds?.length === 0) return;

    const unsubscribeMilestones = realtimeService?.subscribeToMilestones(goalIds, (payload) => {
      console.log('Real-time milestone update:', payload);
      loadGoalsData();
    });

    return () => {
      unsubscribeMilestones();
    };
  }, [goals]);

  // Individual data loading functions for real-time updates
  const loadGoalsData = async () => {
    try {
      const goalsData = await getActiveGoals();
      setGoals(goalsData);
      // Also update summary when goals change
      const summaryData = await getGoalProgressSummary();
      setSummary(summaryData);
    } catch (err) {
      console.error('Error loading goals:', err);
    }
  };

  const loadAchievementsData = async () => {
    try {
      const badgesData = await getAchievementBadges();
      setBadges(badgesData);
    } catch (err) {
      console.error('Error loading achievements:', err);
    }
  };

  const loadStreakData = async () => {
    try {
      const streakData = await getPracticeStreak();
      setStreak(streakData);
    } catch (err) {
      console.error('Error loading streak:', err);
    }
  };

  const loadLeaderboardData = async () => {
    try {
      const leaderboardData = await getLeaderboard(10);
      setLeaderboard(leaderboardData);
    } catch (err) {
      console.error('Error loading leaderboard:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [goalsData, streakData, badgesData, summaryData, leaderboardData] = await Promise.all([
        getActiveGoals(),
        getPracticeStreak(),
        getAchievementBadges(),
        getGoalProgressSummary(),
        getLeaderboard(10)
      ]);

      setGoals(goalsData);
      setStreak(streakData);
      setBadges(badgesData);
      setSummary(summaryData);
      setLeaderboard(leaderboardData);
    } catch (err) {
      console.error('Error loading goal data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoalCreated = () => {
    setIsCreateModalOpen(false);
    loadData();
  };

  const handleGoalUpdated = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your goals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="text-red-500 text-5xl mb-4 text-center">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Error Loading Data</h2>
          <p className="text-gray-600 mb-6 text-center">{error}</p>
          <button
            onClick={loadData}
            className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Goal Setting Center - BardPlay</title>
        <meta name="description" content="Set pronunciation improvement goals and track your progress with comprehensive analytics and milestone tracking" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  🎯 Goal Setting Center
                </h1>
                <p className="text-xl text-purple-100 max-w-2xl">
                  Set personalized pronunciation targets, track milestones, and celebrate achievements on your journey to mastery
                </p>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-all transform hover:scale-105 shadow-xl"
              >
                <Plus className="w-6 h-6" />
                Create New Goal
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Target className="w-8 h-8 mb-2" />
                <div className="text-2xl font-bold">{summary?.activeGoals || 0}</div>
                <div className="text-sm text-purple-100">Active Goals</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Award className="w-8 h-8 mb-2" />
                <div className="text-2xl font-bold">{summary?.achievedMilestones || 0}</div>
                <div className="text-sm text-purple-100">Milestones Reached</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Flame className="w-8 h-8 mb-2" />
                <div className="text-2xl font-bold">{streak?.current_streak || 0}</div>
                <div className="text-sm text-purple-100">Day Streak</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <TrendingUp className="w-8 h-8 mb-2" />
                <div className="text-2xl font-bold">{summary?.milestoneProgress || 0}%</div>
                <div className="text-sm text-purple-100">Progress</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm p-2 mb-6 flex gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('goals')}
              className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === 'goals' ?'bg-purple-600 text-white' :'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Target className="w-5 h-5 inline mr-2" />
              My Goals
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === 'analytics' ?'bg-purple-600 text-white' :'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <TrendingUp className="w-5 h-5 inline mr-2" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('achievements')}
              className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === 'achievements' ?'bg-purple-600 text-white' :'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Award className="w-5 h-5 inline mr-2" />
              Achievements
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === 'leaderboard' ?'bg-purple-600 text-white' :'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Users className="w-5 h-5 inline mr-2" />
              Leaderboard
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-6">
              {activeTab === 'goals' && (
                <>
                  {/* Progress Overview */}
                  <ProgressOverview summary={summary} />

                  {/* Active Goals */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">Active Goals</h2>
                      <span className="text-sm text-gray-500">
                        {goals?.length} {goals?.length === 1 ? 'goal' : 'goals'}
                      </span>
                    </div>
                    
                    {goals?.length === 0 ? (
                      <div className="text-center py-12">
                        <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Goals</h3>
                        <p className="text-gray-600 mb-6">
                          Start your pronunciation improvement journey by creating your first goal!
                        </p>
                        <button
                          onClick={() => setIsCreateModalOpen(true)}
                          className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
                        >
                          <Plus className="w-5 h-5" />
                          Create Your First Goal
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {goals?.map((goal) => (
                          <GoalCard
                            key={goal?.id}
                            goal={goal}
                            onUpdate={handleGoalUpdated}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Milestone Tracker */}
                  {goals?.length > 0 && (
                    <MilestoneTracker goals={goals} />
                  )}
                </>
              )}

              {activeTab === 'analytics' && (
                <AnalyticsVisualization />
              )}

              {activeTab === 'achievements' && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Achievement Collection</h2>
                  
                  {badges?.length === 0 ? (
                    <div className="text-center py-12">
                      <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Badges Yet</h3>
                      <p className="text-gray-600">
                        Complete goals and reach milestones to earn achievement badges!
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {badges?.map((badge) => (
                        <div
                          key={badge?.id}
                          className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border-2 border-yellow-200"
                        >
                          <div className="text-4xl mb-3">🏆</div>
                          <h3 className="font-bold text-lg text-gray-900 mb-2">{badge?.badge_name}</h3>
                          <p className="text-sm text-gray-600 mb-3">{badge?.badge_description}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="w-4 h-4" />
                            {new Date(badge.earned_at)?.toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'leaderboard' && (
                <LeaderboardPanel leaderboard={leaderboard} />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Streak Display */}
              <StreakDisplay streak={streak} />

              {/* Recent Badges */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Recent Achievements
                </h3>
                {badges?.slice(0, 3)?.map((badge) => (
                  <div key={badge?.id} className="flex items-start gap-3 mb-4 last:mb-0">
                    <div className="text-2xl">🏆</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm text-gray-900 truncate">{badge?.badge_name}</h4>
                      <p className="text-xs text-gray-500">
                        {new Date(badge.earned_at)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
                {badges?.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No achievements yet. Keep practicing!
                  </p>
                )}
              </div>

              {/* Motivation Card */}
              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">💪 Stay Motivated</h3>
                <p className="text-sm text-gray-700 mb-4">
                  "The only way to do great work is to love what you do." - Keep practicing and you'll master Shakespeare's pronunciation!
                </p>
                <div className="flex items-center gap-2 text-sm text-purple-700">
                  <Play className="w-4 h-4" />
                  <span>Practice today to maintain your streak!</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create Goal Modal */}
        {isCreateModalOpen && (
          <CreateGoalModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onGoalCreated={handleGoalCreated}
          />
        )}
      </div>
    </>
  );
};

export default StudentGoalSettingCenter;